import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, tap, switchMap, startWith, take, takeUntil, exhaustMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { DealerActions } from './dealer.actions';
import { Router } from '@angular/router';
import { DealerCandidateService } from '../../api-services/dealer/dealer_candidate.service';
import { DealerService } from '../../api-services/dealer/dealer.service';
import { DealerChildrenService } from '../../api-services/dealer/dealer_children.service';
import { DealerUserService } from '../../api-services/dealer/dealer_user.service';
import { AppActions } from '../app/app.actions';
import { DealerEquipmentService } from '../../api-services/dealer/dealer_equipment.service';
import { DealerInviteUserService } from '../../api-services/dealer/dealer_invite_user.service';
import { DealerRegisterService } from '../../api-services/dealer/dealer_register.service';
import { DealerSkinService } from '../../api-services/dealer/dealer_skin.service';
import { DealerLogoService } from '../../api-services/dealer/dealer_logo.service';
import { EquipmentActions } from '../equipment/equipment.actions';
import { DealerAcceptInviteService } from '../../api-services/dealer/dealer_invite_accept.service';
import { DashboardService } from '../../api-services/dashboard/dashboard.service';
import { PermissionService } from '../../local-services/permission.service';
import { AccountsActions } from '../account/account.actions';
import { DealerAccountService } from '../../api-services/dealer/dealer_account.service';
import { DealerSubdealerService } from '../../api-services/dealer/dealer_subdealer.service';

@Injectable()

export class DealerEffects {
  private actions$ = inject(Actions);

  constructor(
    private dealerService: DealerService,
    private dealerCandidateService: DealerCandidateService,
    private dealerChildrenService: DealerChildrenService,
    private dealerSubdealerService: DealerSubdealerService,
    private dashboardService: DashboardService,
    private dealerUserService: DealerUserService,
    private dealerEquipmentService: DealerEquipmentService,
    private dealerAccountService: DealerAccountService,
    private dealerInviteUserService: DealerInviteUserService,
    private dealerRegisterService: DealerRegisterService,
    private dealerSkinService: DealerSkinService,
    private dealerLogoService: DealerLogoService,
    private dealerAcceptService: DealerAcceptInviteService,
    private router: Router,
    private permissionService: PermissionService
  ) { }

  loadDealer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.loadDealer),
      exhaustMap(() =>
        this.dealerService.getDealer().pipe(
          switchMap((data) => [
            DealerActions.loadDealerSuccess({ data }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
          takeUntil(this.actions$.pipe(ofType(DealerActions.unsetDealerStore)))
        )
      )
    );
  });

  updateDealer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.updateDealer),
      concatMap(({ payload }) =>
        this.dealerService.updateDealer(payload).pipe(
          switchMap((data) => [
            DealerActions.updateDealerSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            DealerActions.loadDealer(),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadMembers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.loadMembers),
      concatMap(() =>
        this.dealerUserService.getAllUsers().pipe(
          switchMap((data) => [
            DealerActions.loadMembersSuccess({ data }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadMemberById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.loadMemberById),
      concatMap(({ id }) =>
        this.dealerUserService.getUserById(id).pipe(
          switchMap((data) => [
            DealerActions.loadMemberByIdSuccess({ data }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  inviteMember$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.inviteMember),
      concatMap(
        (payload) =>
          this.dealerInviteUserService.inviteUser(payload.email).pipe(
            switchMap(() => [
              DealerActions.inviteMemberSuccess(),
              AppActions.setLoading({ loading: false }),
              DealerActions.loadMembers(),
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  removeMember$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.removeMember),
      concatMap(({ id }) =>
        this.dealerUserService.removeUser(id).pipe(
          switchMap(() => [
            DealerActions.removeMemberSuccess(),
            AppActions.setLoading({ loading: false }),
            DealerActions.loadMembers(),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  loadSubdealers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.loadSubdealers),
      exhaustMap(() =>
        this.permissionService.isAllowed('listSubDealers').pipe(
          take(1),
          switchMap(isAllowed => {
            if (isAllowed) {
              return this.dealerChildrenService.getChildrenDealers().pipe(
                switchMap((data) => [
                  DealerActions.loadSubdealersSuccess({ data }),
                  // AppActions.setLoading({ loading: false }),
                ]),
                catchError((error) => of(AppActions.setError({ error }))),
                // startWith(AppActions.setLoading({ loading: true }))
              );
            } else {
              return this.dashboardService.getChildren().pipe(
                switchMap((data) => [
                  DealerActions.loadSubdealersSuccess({ data }),
                  // AppActions.setLoading({ loading: false }),
                ]),
                catchError((error) => of(AppActions.setError({ error }))),
                // startWith(AppActions.setLoading({ loading: true }))
              );
            }
          })
        )
      )
    );
  });

  createSubdealer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.createSubdealer),
      concatMap(({ payload }) =>
        this.dealerRegisterService.createDealer(payload).pipe(
          switchMap((data) => [
            DealerActions.createSubdealerSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            DealerActions.loadSubdealers(),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  deleteSubdealer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.deleteSubdealer),
      concatMap(({ subdealer_id }) =>
        this.dealerSubdealerService.deleteSubdealer(subdealer_id).pipe(
          switchMap(() => [
            DealerActions.deleteSubdealerSuccess(),
            AppActions.setLoading({ loading: false }),
            DealerActions.loadSubdealers(),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  assignEquipmentsToSubdealer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.assignEquipmentsToSubdealer),
      concatMap((action) =>
        this.dealerEquipmentService
          .assignEquipmentsToSubdealer(
            action.subdealer_id,
            action.equipments_ids
          )
          .pipe(
            switchMap((data) => [
              DealerActions.assignEquipmentsToSubdealerSuccess({ data }),
              AppActions.setLoading({ loading: false }),
              EquipmentActions.loadEquipmentById({ id: action.equipments_ids[0] })
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  assignAccountsToSubdealer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.assignAccountsToSubdealer),
      concatMap((action) =>
        this.dealerAccountService
          .assignAccountsToSubdealer(
            action.subdealer_id,
            action.accounts_ids
          )
          .pipe(
            switchMap((data) => [
              DealerActions.assignAccountsToSubdealerSuccess({ data }),
              AppActions.setLoading({ loading: false }),
              AccountsActions.loadAccountById({ id: action.accounts_ids[0] })
            ]),
            catchError((error) => of(AppActions.setError({ error }))),
            startWith(AppActions.setLoading({ loading: true }))
          )
      )
    );
  });

  loadSkin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.loadSkin),
      concatMap(() =>
        this.dealerSkinService.getSkin().pipe(
          switchMap((data) => [
            DealerActions.loadSkinSuccess({ data }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  updateSkin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.updateSkin),
      concatMap(({ payload }) =>
        this.dealerSkinService.updateSkin(payload).pipe(
          switchMap((data) => [
            DealerActions.updateSkinSuccess({ data }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  uploadLogo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.uploadLogo),
      concatMap(({ payload }) =>
        this.dealerLogoService.uploadDealerLogo(payload).pipe(
          switchMap((data) => [
            DealerActions.uploadLogoSuccess({ data }),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  sendNewDealerRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.sendNewDealerRequest),
      concatMap((action) =>
        this.dealerCandidateService.newDealerRequest(action.payload).pipe(
          switchMap(() => [
            DealerActions.sendNewDealerRequestSuccess(),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });
  reloadAfterSendNewDealerRequestSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DealerActions.sendNewDealerRequestSuccess),
        tap(() => this.router.navigate(['/login']))
      ),
    { dispatch: false }
  );

  resendDealerInvitation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.resendDealerInvitation),
      concatMap(({ subdealerId }) =>
        this.dealerRegisterService.resendDealerInvitation(subdealerId).pipe(
          switchMap(() => [
            DealerActions.resendDealerInvitationSuccess(),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

  updateUserPermissions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.updateMemberPermissions),
      concatMap(({ userId, permissions }) =>
        this.dealerUserService.updateUserPermissions(userId, permissions).pipe(
          switchMap(data => [
            DealerActions.updateMemberPermissionsSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        ))
    );
  });

  acceptDealerInvite$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DealerActions.acceptDealerInvite),
      concatMap(({ userId }) =>
        this.dealerAcceptService.inviteAccept(userId).pipe(
          switchMap(() => [
            DealerActions.acceptDealerInviteSuccess(),
            AppActions.setLoading({ loading: false }),
          ]),
          catchError((error) => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true }))
        )
      )
    );
  });

}
